// Import dependencies
import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import '../styles/CelendarStyles.css'; // Import the custom styles
import { useSelector, useDispatch } from 'react-redux';
import { getWorkerAttendance_employerView } from '../features/workHistory/workHistroySlice';

const AttendanceByWorker = () => {
    
    const dispatch = useDispatch();
    // State for date range selection (7 days max)
    const [dateRange, setDateRange] = useState([new Date(), new Date()]);
    const [dateFrom, dateTo] = dateRange;

    // from redux grab the selected worker data, profile picture url, workerId, workerName, etc.
     const { worker } = useSelector(state => state.selectedWorker);
     const {employerView_byWorker} = useSelector(state => state.workHistory);
    // Handle calendar date range changes
    const onChange = (range) => {
        if (range.length === 2) {
            const [start, end] = range;
            const diffTime = Math.abs(end - start);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            // Limit the date range to a maximum of 7 days
            if (diffDays > 6) {
                const newEnd = new Date(start);
                newEnd.setDate(start.getDate() + 6);
                setDateRange([start, newEnd]);
            } else {
                setDateRange(range);
            }
        } else {
            setDateRange(range);
        }
    };

    // Fetch attendance data when date range changes
    useEffect(() => {
        // Format date as YYYY-MM-DD
        const formatDate = (date) => date.toISOString().slice(0, 10);
        const payload = {
            workerID: worker.id,
            dateOne: formatDate(dateFrom),
            dateTwo: formatDate(dateTo),
        };
        dispatch(getWorkerAttendance_employerView(payload));
    }, [dateRange, dispatch, worker.id, dateFrom, dateTo]);


    // Process attendance data to group by date and handle multiple locations
    const processAttendanceData = () => {
        // Helper to format date as MM/DD/YYYY
        const formatDateKey = (dateStr) => {
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-US');
        };

        // Group records by date
        const recordsByDate = {};
        employerView_byWorker.forEach(record => {
            if (!record.timeIn) return;

            let hoursWorked = 0;
            if (record.timeIn && record.timeOut) {
                const timeIn = new Date(record.timeIn);
                const timeOut = new Date(record.timeOut);
                hoursWorked = (timeOut - timeIn) / (1000 * 60 * 60);
            }

            const newRecord = { ...record, hoursWorked };

            const dateKey = formatDateKey(record.timeIn);
            if (!recordsByDate[dateKey]) recordsByDate[dateKey] = [];
            recordsByDate[dateKey].push(newRecord);
        });

        // Build date range
        const dates = [];
        const startDate = new Date(dateFrom);
        const endDate = new Date(dateTo);

        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            const dateString = d.toLocaleDateString('en-US');
            const dayOfWeek = d.toLocaleDateString('en-US', { weekday: 'long' });
            const dateRecords = recordsByDate[dateString] || [];
            dates.push({ 
                date: dateString, 
                dayOfWeek, 
                records: dateRecords,
                hasData: dateRecords.length > 0
            });
        }

        return dates;
    };

    const selectedDates = processAttendanceData();
    const totalHours = selectedDates.reduce((total, day) => {
        return total + day.records.reduce((dayTotal, record) => dayTotal + record.hoursWorked, 0);
    }, 0);
    
    return (
        <div className="flex flex-col items-center p-4">
            {/* Top Section: Worker Info + Calendar */}
            <div className="w-full flex flex-col md:flex-row md:justify-center md:items-start gap-8 mb-6">
                {/* Calendar */}
                <div className="calendar-wrapper w-full md:w-auto">
                    <Calendar
                        selectRange={true}
                        onChange={onChange}
                        value={dateRange}
                    />
                    <div className="date-info text-center">
                        <p className="mt-4">Date From: {dateFrom.toDateString()}</p>
                        <p>Date To: {dateTo.toDateString()}</p>
                    </div>
                </div>

                {/* Worker Card */}
                <div className="flex flex-col items-center bg-white rounded-lg shadow-md p-6 w-full md:w-auto md:min-w-[300px]">
                    {worker.profilePicture && (
                        <img 
                            src={worker.profilePicture} 
                            alt={`${worker.name} profile`}
                            className="w-32 h-32 rounded-full object-cover border-4 border-teal shadow-lg mb-3"
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                    )}
                    <h1 className="text-xl font-kanit text-teal mb-1 text-center">{worker.name} {worker.lastName}</h1>
                    <p className="text-gray-600 text-sm mb-2">$/Hr: {worker.rate}</p>
                </div>
            </div>

            {/* Bottom Section: Attendance Table */}
            <div className="w-full max-w-5xl">
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-teal text-ivory">
                            <tr>
                                <th className="px-6 py-3 text-left font-kanit">Date</th>
                                <th className="px-6 py-3 text-left font-kanit">Location(s)</th>
                                <th className="px-6 py-3 text-left font-kanit">Hours</th>
                                <th className="px-6 py-3 text-left font-kanit">Hours Worked</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedDates.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-8 text-center text-gray-500 font-sans italic">
                                        Select a date range from the calendar.
                                    </td>
                                </tr>
                            ) : (
                                selectedDates.map((selectedDate, index) => (
                                    selectedDate.hasData ? (
                                        selectedDate.records.map((record, recIdx) => (
                                            <tr key={recIdx} className="border-b hover:bg-gray-50">
                                                {recIdx === 0 && (
                                                    <td className="px-6 py-4 font-sans text-charcoal" rowSpan={selectedDate.records.length}>
                                                        <div>{selectedDate.date}</div>
                                                        <div className="text-sm text-gray-500">{selectedDate.dayOfWeek}</div>
                                                    </td>
                                                )}
                                                <td className="px-6 py-4 font-sans text-charcoal">
                                                    <div className="bg-gray-100 px-2 py-1 rounded text-sm inline-block">
                                                        {record.jobsiteName || 'Unknown Location'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 font-sans text-charcoal">
                                                    <div className="text-base">
                                                        <span className="font-normal text-sm">In:</span> {record.timeIn ? new Date(record.timeIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                                                    </div>
                                                    <div className="text-base">
                                                        <span className="font-normal text-sm">Out:</span> {record.timeOut ? new Date(record.timeOut).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : <span className="text-red-500">Did not clock out</span>}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 font-sans text-charcoal">
                                                    {record.hoursWorked > 0 ? record.hoursWorked.toFixed(2) : 'N/A'}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr key={index}>
                                            <td className="px-6 py-4 font-sans text-charcoal">{selectedDate.date}
                                                <div className="text-sm text-gray-500">{selectedDate.dayOfWeek}</div>
                                            </td>
                                            <td className="px-6 py-4 font-sans text-charcoal" colSpan={3}>
                                                <span className="text-gray-500 italic">No work recorded</span>
                                            </td>
                                        </tr>
                                    )
                                ))
                            )}
                        </tbody>
                        <tfoot className="bg-gray-100">
                            <tr>
                                <td colSpan={3} className="px-6 py-4 text-right font-kanit text-charcoal font-bold">Period Total</td>
                                <td className="px-6 py-4 font-sans text-charcoal font-bold">{totalHours.toFixed(2)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AttendanceByWorker;